/**
 * Admin home screen — smartphone-style icon grid for admin role on mobile/tablet (<1024px).
 *
 * Two-level navigation :
 *  1. Top-level : 12 admin tiles. Tiles with sub-menus expand inline to a
 *     secondary grid of sub-tiles. Tiles without sub-menus navigate directly.
 *  2. The current open category is tracked locally — clicking another
 *     category collapses the previous one. A back arrow returns to the
 *     top-level grid.
 *
 * Designed to be rendered as the `/` page content when user.role === 'admin'
 * AND viewport is <1024px. On desktop, the regular Dashboard widgets are used.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  House, CalendarDots, NotePencil, Envelope, ShieldCheck, Buildings, HardHat,
  Truck, Receipt, Warehouse, AddressBook, ChatCircle, ArrowLeft,
  Users, AirplaneTilt, ClockCounterClockwise, FileText, FilePdf, ChartBar,
  ShieldStar, Clock, Folder, Briefcase, Calendar, CurrencyEur, ShoppingCart,
  ChartPie, BookOpen, Wallet, Receipt as ReceiptIcon, ChartLineUp, Cube, Wrench, Car, Eye,
  Megaphone, NewspaperClipping,
} from '@phosphor-icons/react';

// Top-level tiles with their gradient pair (135deg) and optional children sub-tiles
const TILES = [
  { to: '/planning',     icon: CalendarDots, label: 'Planning',     grad: ['#7C3AED', '#A855F7'] },
  { to: '/validate',     icon: ClockCounterClockwise, label: 'Validation heures', grad: ['#0EA5E9', '#38BDF8'] },
  { to: '/fil-actualite', icon: NewspaperClipping,    label: 'Fil d\'actualité',  grad: ['#F59E0B', '#FCD34D'] },
  { to: '/notes-taches', icon: NotePencil,   label: 'Notes & Tâches', grad: ['#0891B2', '#22D3EE'] },
  { to: '/correspondance', icon: Envelope,   label: 'Correspondance', grad: ['#0369A1', '#0EA5E9'] },
  {
    icon: ShieldCheck, label: 'Administration', grad: ['#BE185D', '#EC4899'],
    children: [
      { to: '/documents?tab=salaries_merged', icon: Users,             label: 'Salariés' },
      { to: '/documents?tab=conges',          icon: AirplaneTilt,      label: 'Congés' },
      { to: '/documents?tab=maladie',         icon: Clock,             label: 'Arrêts & absences' },
      { to: '/documents?tab=validate',        icon: ClockCounterClockwise, label: 'Validation heures' },
      { to: '/documents?tab=company_docs',    icon: FileText,          label: 'Docs obligatoires' },
      { to: '/documents?tab=employee_docs',   icon: Folder,            label: 'Docs salariés' },
      { to: '/documents?tab=admin_docs',      icon: FilePdf,           label: 'Archives' },
      { to: '/reports',                       icon: ChartBar,          label: 'Rapports' },
      { to: '/access',                        icon: ShieldStar,        label: 'Accès' },
      { to: '/history',                       icon: ClockCounterClockwise, label: 'Historique' },
      { to: '/apercus-interfaces',            icon: Eye,               label: 'Aperçus interfaces' },
    ],
  },
  {
    icon: Buildings, label: 'Chantiers', grad: ['#EA580C', '#FB923C'],
    children: [
      { to: '/projects?tab=chantiers',             icon: Buildings,  label: 'Chantiers' },
      { to: '/opportunites',                       icon: Briefcase,  label: 'Opportunités' },
      { to: '/programmation',                      icon: Calendar,   label: 'Programmation' },
      { to: '/projects?tab=facturation',           icon: ReceiptIcon, label: 'Facturation' },
      { to: '/projects?tab=courses',               icon: ShoppingCart, label: 'Courses' },
      { to: '/projects?tab=commandes',             icon: FileText,   label: 'Commandes' },
      { to: '/projects?tab=statistiques',          icon: ChartPie,   label: 'Statistiques' },
      { to: '/projects?tab=bibliotheque_catalogue', icon: BookOpen,  label: 'Bibliothèque' },
    ],
  },
  { to: '/sous-traitance', icon: HardHat, label: 'Sous-traitance', grad: ['#16A34A', '#4ADE80'] },
  { to: '/fournisseurs',   icon: Truck,   label: 'Fournisseurs',   grad: ['#0F766E', '#14B8A6'] },
  {
    icon: Receipt, label: 'Finance', grad: ['#DC2626', '#F87171'],
    children: [
      { to: '/finance?tab=depenses',    icon: Wallet,      label: 'Dépenses' },
      { to: '/finance?tab=notes_frais', icon: ReceiptIcon, label: 'Notes de frais' },
      { to: '/finance?tab=charges',     icon: Calendar,    label: 'Charges mensuelles' },
      { to: '/finance?tab=tva',         icon: CurrencyEur, label: 'TVA' },
    ],
  },
  {
    icon: Warehouse, label: 'Ressources', grad: ['#1D4ED8', '#3B82F6'],
    children: [
      { to: '/resources?tab=materiaux_stocks', icon: Cube,     label: 'Stocks matériaux' },
      { to: '/resources?tab=materiel',         icon: Wrench,   label: 'Matériel' },
      { to: '/resources?tab=vehicules',        icon: Car,      label: 'Véhicules' },
    ],
  },
  { to: '/contacts', icon: AddressBook, label: 'Contacts', grad: ['#CA8A04', '#FACC15'] },
  { to: '/chat',     icon: ChatCircle,  label: 'Chat',     grad: ['#9333EA', '#C084FC'] },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/gi, '-').replace(/^-|-$/g, '');

// Reusable tile (used for both top-level and sub-tiles, with different sizes)
const Tile = ({ to, icon: Icon, label, grad, onClick, testId, iconSize = 42, label2line = true }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'flex-start', gap: 6, padding: 0,
      background: 'transparent', border: 'none', cursor: 'pointer',
      outline: 'none', WebkitTapHighlightColor: 'transparent',
    }}
  >
    <span
      style={{
        position: 'relative', width: '100%', aspectRatio: '1 / 1',
        borderRadius: '22px',
        background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
        boxShadow: '0 6px 14px rgba(15, 23, 42, 0.14), inset 0 -2px 8px rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', transition: 'transform 0.15s ease',
        maxWidth: 130, margin: '0 auto',
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.94)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.94)'; }}
      onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <Icon size={iconSize} weight="duotone" />
    </span>
    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0F172A', textAlign: 'center', lineHeight: 1.2, maxWidth: 130, minHeight: label2line ? '2.2em' : undefined }}>
      {label}
    </span>
  </button>
);

const AdminHomeScreen = ({ user }) => {
  const navigate = useNavigate();
  const [openTile, setOpenTile] = useState(null);   // label of the open sub-grid, or null

  const handleTileClick = (tile) => {
    if (tile.children && tile.children.length > 0) {
      setOpenTile(tile.label);
    } else {
      navigate(tile.to);
    }
  };

  // Sub-palette : 8 distinct gradients re-used cyclically for child tiles
  const SUB_PALETTE = [
    ['#1D4ED8', '#3B82F6'], ['#7C3AED', '#A855F7'], ['#0891B2', '#22D3EE'],
    ['#16A34A', '#4ADE80'], ['#EA580C', '#FB923C'], ['#BE185D', '#EC4899'],
    ['#CA8A04', '#FACC15'], ['#0F766E', '#14B8A6'],
  ];

  // Sub-grid view : show children of the currently open tile
  if (openTile) {
    const tile = TILES.find((t) => t.label === openTile);
    if (!tile) { setOpenTile(null); return null; }
    return (
      <div data-testid={`admin-home-sub-${slug(openTile)}`} style={{ padding: '12px 4px 32px' }}>
        <div style={{ padding: '8px 12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => setOpenTile(null)}
            data-testid="admin-home-back"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', color: '#0F172A' }}
            aria-label="Retour"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Menu
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: '1.4rem', fontWeight: 700, color: '#0F172A' }}>
              {tile.label}
            </h1>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '14px', padding: '0 12px', maxWidth: 880, margin: '0 auto' }}>
          {tile.children.map((c, idx) => {
            const grad = SUB_PALETTE[idx % SUB_PALETTE.length];
            return (
              <Tile
                key={c.to}
                to={c.to}
                icon={c.icon || tile.icon}
                label={c.label}
                grad={grad}
                onClick={() => navigate(c.to)}
                testId={`admin-home-tile-${slug(tile.label)}-${slug(c.label)}`}
                iconSize={36}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Top-level grid
  return (
    <div data-testid="admin-home-screen" style={{ padding: '12px 4px 32px' }}>
      <div style={{ padding: '8px 12px 18px' }}>
        <div style={{ fontSize: '0.78rem', color: '#5E5F66', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Console admin
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: '1.6rem', fontWeight: 700, color: '#0F172A' }}>
          Bonjour {user?.name?.split(' ')[0] || 'Admin'}
        </h1>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '14px', padding: '0 12px', maxWidth: 880, margin: '0 auto' }}>
        {TILES.map((t) => (
          <Tile
            key={t.label}
            to={t.to}
            icon={t.icon}
            label={t.label}
            grad={t.grad}
            onClick={() => handleTileClick(t)}
            testId={`admin-home-tile-${slug(t.label)}`}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminHomeScreen;
